import React from 'react';
import { Box, Button, FormControlLabel, Typography, Tooltip, Checkbox, Switch, FormGroup, Popover, Paper, IconButton, Menu, MenuItem, useTheme, Dialog, DialogContent, DialogActions, CircularProgress, Radio, RadioGroup, Divider, ListItemIcon, ListItemText, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import Legend from './OrgGraphLegend';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PhysicsSettings, { PhysicsParams } from './PhysicsSettingsPanel';
import SettingsIcon from '@mui/icons-material/Settings';
import LabelIcon from '@mui/icons-material/Label';
import DownloadIcon from '@mui/icons-material/Download';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CloseIcon from '@mui/icons-material/Close';
import { NodeLabelSettings, EdgeLabelSettings } from './types';
import { OrgGraphVisRef } from './OrgGraphVis';
import { GraphType } from './graphType';
import Documentation from './Documentation';
import TimeSliderPanel from './TimeSliderPanel';
import { useTranslation } from 'react-i18next';

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return target.isContentEditable || Boolean(target.closest('input, textarea, [contenteditable="true"]'));
}

// Update the props interface
interface OrgGraphTopPanelProps {
  disabled: boolean;
  onShowBadLinksUpdated: (showBadLinks: boolean) => void;
  onLinkingOptionsUpdated: (selectedOptions: string[]) => void; // Changed to accept array of selected options
  showFilterButtonsValue: boolean;
  onBack?: () => void;
  onForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
  onToggleFullscreen?: () => void;  
  isFullscreen?: boolean;           
  availableGraphTypes?: string[];   // All available graph types
  selectedGraphTypes?: string[];    // Currently selected graph types
  showBadLinks?: boolean;           // Whether to show only bad paths
  activeRelationshipsOnly?: boolean;
  onActiveRelationshipsOnlyChange?: (activeRelationshipsOnly: boolean) => void;
  onRestabilize?: () => void;       // Handler for full restabilization
  onPhysicsSettingsChange: (newSettings: PhysicsParams) => void;
  physicsSettings: PhysicsParams;
  showNewAdditions?: boolean;
  onClearNewAdditions?: () => void;
  nodeLabelSettings?: NodeLabelSettings;
  edgeLabelSettings?: EdgeLabelSettings;
  onNodeLabelSettingsChange?: (settings: NodeLabelSettings) => void;
  onEdgeLabelSettingsChange?: (settings: EdgeLabelSettings) => void;
  graphVisRef?: React.RefObject<OrgGraphVisRef>;  // Ref to access graph export
  leftSpacerWidth?: string | number;
  legendLeftOffset?: string | number;
  isDarkMode?: boolean;
  onToggleSearch?: () => void;
  isSearchOpen?: boolean;
  onToggleSidePanel?: () => void;
  isSidePanelOpen?: boolean;
  onToggleSummary?: () => void;
  isSummaryOpen?: boolean;
  topBarContent?: React.ReactNode;
  enableCompanyComments?: boolean;
  showCompanyComments?: boolean;
  onToggleCompanyComments?: () => void;
  enableIvoLicenses?: boolean;
  enableLeasedCars?: boolean;
  enableTenantCases?: boolean;
  
  // Time Slider Props
  minDate?: string | null;
  maxDate?: string | null;
  selectedDate?: string | null;
  onDateChange?: (date: string | null) => void;
}

const OrgGraphTopPanel = (props: OrgGraphTopPanelProps) => {
  const { t } = useTranslation();
  const {
    disabled,
    onShowBadLinksUpdated,
    onLinkingOptionsUpdated,
    showFilterButtonsValue,
    onBack,
    onForward,
    canGoBack = false,
    canGoForward = false,
    availableGraphTypes = ['all'], // Default to 'all' if not provided
    selectedGraphTypes = ['all'], // Default to 'all' if not provided
    showBadLinks: showBadLinksProp = false,
    activeRelationshipsOnly = false,
    onActiveRelationshipsOnlyChange,
    physicsSettings,
    onPhysicsSettingsChange,
    legendLeftOffset = '12px',
    isDarkMode: isDarkModeProp,
    topBarContent,
    enableCompanyComments = false,
    showCompanyComments = true,
    onToggleCompanyComments,
    enableIvoLicenses = false,
    enableLeasedCars = false,
    enableTenantCases = false,
    minDate,
    maxDate,
    selectedDate,
    onDateChange
  } = props;
  
  const theme = useTheme();
  // Use prop if provided, otherwise fall back to theme
  const isDarkMode = isDarkModeProp ?? theme.palette.mode === 'dark';
  
  const textColor = isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  const disabledColor = isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)';

  // Log the available graph types when component mounts or when they change
  React.useEffect(() => {
    console.log('OrgGraphTopPanel: Available graph types:', availableGraphTypes);
  }, [availableGraphTypes]);
  
  const [showBadLinks, setShowBadLinks] = React.useState(showBadLinksProp);
  const [selectedTypes, setSelectedTypes] = React.useState<string[]>(selectedGraphTypes);
  const [showLegend, setShowLegend] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  const [isShortcutRevealKeyPressed, setIsShortcutRevealKeyPressed] = React.useState(false);
  const [isTopPanelHovered, setIsTopPanelHovered] = React.useState(false);
  const [physicsAnchorEl, setPhysicsAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [labelAnchorEl, setLabelAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [openTimeSlider, setOpenTimeSlider] = React.useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = React.useState<HTMLElement | null>(null);
  const moreButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const backButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const forwardButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const legendButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const restabilizeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const timeSliderButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const sidePanelButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const summaryButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const fullscreenButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const labelSettingsButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const handlePhysicsClick = () => {
    setPhysicsAnchorEl((currentAnchorEl) => currentAnchorEl ? null : moreButtonRef.current);
  };

  const handlePhysicsClose = () => {
    setPhysicsAnchorEl(null);
  };

  const openPhysics = Boolean(physicsAnchorEl);
  const physicsId = openPhysics ? 'physics-popover' : undefined;

  const handleTimeSliderClick = () => {
    setOpenTimeSlider(current => !current);
  };

  const handleTimeSliderClose = () => {
    setOpenTimeSlider(false);
    timeSliderButtonRef.current?.focus();
  };

  const timeSliderId = 'time-slider-panel';

  const handleLabelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLabelAnchorEl((currentAnchorEl) => currentAnchorEl ? null : event.currentTarget);
  };

  const handleLabelClose = () => {
    setLabelAnchorEl(null);
  };

  const handleNodeLabelToggle = (key: keyof NodeLabelSettings) => {
    if (props.onNodeLabelSettingsChange && props.nodeLabelSettings) {
      props.onNodeLabelSettingsChange({
        ...props.nodeLabelSettings,
        [key]: !props.nodeLabelSettings[key]
      });
    }
  };

  const handleEdgeLabelToggle = (key: keyof EdgeLabelSettings) => {
    if (props.onEdgeLabelSettingsChange && props.edgeLabelSettings) {
      props.onEdgeLabelSettingsChange({
        ...props.edgeLabelSettings,
        [key]: !props.edgeLabelSettings[key]
      });
    }
  };

  const [exportAnchorEl, setExportAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [exportScale, setExportScale] = React.useState<number>(12);
  const [maxScale, setMaxScale] = React.useState<number>(36);

  const handleExportClick = () => {
    if (exportAnchorEl) {
      setExportAnchorEl(null);
      return;
    }

    setExportAnchorEl(moreButtonRef.current);
    if (props.graphVisRef?.current?.getMaxScale) {
        // Get the max safe scale from the graph component
        const calculatedMax = props.graphVisRef.current.getMaxScale();
        setMaxScale(calculatedMax);
        // Ensure current value is within bounds
        if (exportScale > calculatedMax) {
            setExportScale(calculatedMax);
        }
    }
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const openExport = Boolean(exportAnchorEl);
  const exportId = openExport ? 'export-popover' : undefined;

  const [isExporting, setIsExporting] = React.useState(false);
  const handleHelpToggle = () => {
    setShowHelp((currentShowHelp) => !currentShowHelp);
  };

  const handleExportSubmit = async () => {
    if (props.graphVisRef?.current) {
        setIsExporting(true);
        // Pass the selected scale to the export function
        try {
            await props.graphVisRef.current.exportGraphAsPNG(undefined, exportScale);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
            handleExportClose();
        }
    } else {
        handleExportClose();
    }
  };

  const handleExportSVG = async () => {
    if (props.graphVisRef?.current) {
        setIsExporting(true);
        try {
            await props.graphVisRef.current.exportGraphAsSVG();
        } catch (error) {
            console.error('SVG export failed:', error);
        } finally {
            setIsExporting(false);
            handleExportClose();
        }
    } else {
        handleExportClose();
    }
  };

  // Update local state when props change
  React.useEffect(() => {
    setSelectedTypes(selectedGraphTypes);
  }, [selectedGraphTypes]);

  const shortcutButtons = React.useMemo(
    () => ({
      b: backButtonRef,
      n: forwardButtonRef,
      l: legendButtonRef,
      r: restabilizeButtonRef,
      t: timeSliderButtonRef,
      e: sidePanelButtonRef,
      a: summaryButtonRef,
      f: fullscreenButtonRef,
      v: labelSettingsButtonRef,
    }),
    [],
  );

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openTimeSlider && !event.defaultPrevented) {
        event.preventDefault();
        handleTimeSliderClose();
        return;
      }

      if (event.key === 'Alt') {
        setIsShortcutRevealKeyPressed(true);
      }

      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.repeat || isEditableTarget(event.target)) {
        return;
      }

      const shortcut = event.key.toLowerCase();

      // Invoke overflow actions directly so shortcuts work while the menu is closed.
      const overflowActions: Record<string, { available: boolean; run: () => void }> = {
        c: { available: Boolean(props.showNewAdditions && props.onClearNewAdditions), run: () => props.onClearNewAdditions?.() },
        s: { available: Boolean(showFilterButtonsValue && props.onToggleSearch), run: () => props.onToggleSearch?.() },
        x: { available: showFilterButtonsValue && !disabled, run: handleBadLinksToggle },
        d: { available: showFilterButtonsValue && !disabled, run: handleExportClick },
        p: { available: showFilterButtonsValue, run: handlePhysicsClick },
        h: { available: showFilterButtonsValue, run: handleHelpToggle },
      };
      const overflowAction = overflowActions[shortcut];
      if (overflowAction?.available) {
        event.preventDefault();
        setMoreAnchorEl(null);
        overflowAction.run();
        return;
      }

      const buttonRef = shortcutButtons[shortcut as keyof typeof shortcutButtons];
      const button = buttonRef?.current;

      if (!button || button.disabled) {
        return;
      }

      event.preventDefault();
      button.click();
    };

    window.addEventListener('keydown', handleKeyDown);

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Alt') {
        setIsShortcutRevealKeyPressed(false);
      }
    };

    const handleWindowBlur = () => {
      setIsShortcutRevealKeyPressed(false);
    };

    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  });

  const showShortcutHints = isShortcutRevealKeyPressed || isTopPanelHovered;

  const handleLinkingOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.name;
    const checked = event.target.checked;
    
    let newSelectedTypes: string[];
    
    if (type === 'all') {
      // If "All" is selected, always deselect all others and select only 'all'
      newSelectedTypes = checked ? ['all'] : [];
    } else {
      if (checked) {
        // If a specific type is checked:
        
        // If "All" was previously selected, deselect it and select only the new type
        if (selectedTypes.includes('all')) {
          newSelectedTypes = [type];
        } else {
          // Otherwise, add this type to existing selections (keeping the current selection)
          newSelectedTypes = [...selectedTypes, type];
        }
      } else {
        // If a specific type is unchecked, simply remove it from the selection
        newSelectedTypes = selectedTypes.filter(t => t !== type);
        
        // If no specific type is selected, default to "all"
        if (newSelectedTypes.length === 0) {
          newSelectedTypes = ['all'];
        }
      }
    }
    
    setSelectedTypes(newSelectedTypes);
    onLinkingOptionsUpdated(newSelectedTypes);
  };

  const handleBadLinksToggle = () => {
    const nextShowBadLinks = !showBadLinks;
    setShowBadLinks(nextShowBadLinks);
    onShowBadLinksUpdated(nextShowBadLinks);
  };

  const toggleLegend = () => {
    setShowLegend(prevShowLegend => !prevShowLegend);
  };

  const shortcutTitle = (label: string, shortcut: string) => `${label} (${shortcut.toUpperCase()})`;

  const renderShortcutBadge = (shortcut: string) => (
    <Box
      sx={{
        position: 'absolute',
        top: -7,
        right: -7,
        minWidth: 18,
        height: 18,
        px: 0.5,
        borderRadius: '999px',
        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.14)'}`,
        backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.96)',
        boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 2,
        opacity: showShortcutHints ? 1 : 0,
        transform: showShortcutHints ? 'translateY(0) scale(1)' : 'translateY(-2px) scale(0.92)',
        transition: 'opacity 0.18s ease, transform 0.18s ease',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.62rem',
          lineHeight: 1,
          fontWeight: 700,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          color: isDarkMode ? 'rgba(255,255,255,0.88)' : 'rgba(15,23,42,0.82)',
        }}
      >
        {shortcut.toUpperCase()}
      </Typography>
    </Box>
  );

  const renderToolbarButton = (
    shortcut: string, label: string, icon: React.ReactNode,
    onClick: React.MouseEventHandler<HTMLButtonElement> | undefined,
    ref: React.RefObject<HTMLButtonElement>, isDisabled = false, active?: boolean, iconOnly = false,
  ) => (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Tooltip title={shortcutTitle(label, shortcut)}>
        <span>
          <Button ref={ref} onClick={onClick} disabled={isDisabled} size="small"
            aria-label={label} aria-keyshortcuts={shortcut} aria-pressed={active}
            aria-controls={shortcut === 't' && openTimeSlider ? timeSliderId : undefined}
            aria-expanded={shortcut === 't' ? openTimeSlider : undefined}
            startIcon={iconOnly ? undefined : icon}
            sx={{ textTransform: 'none', minWidth: iconOnly ? 32 : 'auto', px: 1,
              color: active ? 'primary.main' : 'text.primary', bgcolor: active ? 'action.selected' : 'transparent' }}>
            {iconOnly ? icon : label}
          </Button>
        </span>
      </Tooltip>
      {renderShortcutBadge(shortcut)}
    </Box>
  );

  const renderMoreItem = (shortcut: string, label: string, icon: React.ReactNode, action: () => void, isDisabled = false, checked?: boolean) => (
    <MenuItem disabled={isDisabled} role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={checked} aria-keyshortcuts={shortcut} onClick={() => { setMoreAnchorEl(null); action(); }}>
      <ListItemIcon>{checked === undefined ? icon : <Checkbox checked={checked} size="small" tabIndex={-1} inputProps={{ 'aria-hidden': true, tabIndex: -1 }} sx={{ p: 0, pointerEvents: 'none' }} />}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>{shortcut.toUpperCase()}</Typography>
    </MenuItem>
  );



  return (
    <div 
      className="org-graph-top-panel"
      onMouseEnter={() => setIsTopPanelHovered(true)}
      onMouseLeave={() => setIsTopPanelHovered(false)}
      style={{
        display: 'flex',
        padding: '12px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '64px',
        gap: '10px',
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transform: 'scale(0.75)',
        transformOrigin: 'top left',
        width: 'calc(100% / 0.75)',
        position: 'relative',
        zIndex: 100,
        flexWrap: 'wrap',
        boxSizing: 'border-box'
      }}
    >
      {topBarContent && <Box sx={{ display: 'flex', alignItems: 'center' }}>{topBarContent}</Box>}
      <Box role="group" aria-label={t('graph.toolbarNavigation')} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {renderToolbarButton('b', t('graph.goBack'), <ArrowBackIcon />, onBack, backButtonRef, disabled || !canGoBack, undefined, true)}
        {renderToolbarButton('n', t('graph.goForward'), <ArrowForwardIcon />, onForward, forwardButtonRef, disabled || !canGoForward, undefined, true)}
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box role="group" aria-label={t('graph.toolbarView')} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {showFilterButtonsValue && props.onNodeLabelSettingsChange && props.nodeLabelSettings && props.onEdgeLabelSettingsChange && props.edgeLabelSettings &&
          renderToolbarButton('v', t('graph.toolbarLabels'), <LabelIcon />, handleLabelClick, labelSettingsButtonRef, false, Boolean(labelAnchorEl))}
        {showFilterButtonsValue && props.onToggleSidePanel && renderToolbarButton('e', t('graph.entityList'), <ListIcon />, props.onToggleSidePanel, sidePanelButtonRef, false, props.isSidePanelOpen)}
        {renderToolbarButton('l', t('graph.toolbarLegend'), <HelpOutlineIcon />, toggleLegend, legendButtonRef, false, showLegend)}
        {showFilterButtonsValue && props.onToggleSummary && renderToolbarButton('a', t('graph.summary'), <AssessmentIcon />, props.onToggleSummary, summaryButtonRef, false, props.isSummaryOpen)}
        {props.onRestabilize && renderToolbarButton('r', t('graph.restabilize'), <RestoreIcon />, props.onRestabilize, restabilizeButtonRef, disabled)}
        {showFilterButtonsValue && minDate && maxDate && onDateChange && renderToolbarButton('t', t('graph.timeFilter'), <HourglassBottomIcon />, handleTimeSliderClick, timeSliderButtonRef, false, openTimeSlider)}
      </Box>
      {showFilterButtonsValue && (
        <>
          <Divider orientation="vertical" flexItem />
          {/* Relationship filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: textColor, mr: 1 }}>
          {t('graph.types')}:
        </Typography>
        <FormGroup row sx={{ gap: 1, flexWrap: 'wrap' }}>
          {availableGraphTypes.map(type => (
          <FormControlLabel
            key={type}
            control={
            <Checkbox
              checked={selectedTypes.includes(type)}
              onChange={handleLinkingOptionChange}
              name={type}
              size="small"
              sx={{
              color: disabledColor,
              padding: '2px',
              '&.Mui-checked': {
                color: theme.palette.primary.main
              }
              }}
            />
            }
            label={
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: textColor }}>
              {t(`graph.graphTypes.${type}`, { defaultValue: GraphType.getDisplayName(type) })}
            </Typography>
            }
            sx={{ margin: 0, ml: 0.5 }}
          />
          ))}
        </FormGroup>
        </Box>
        {onActiveRelationshipsOnlyChange && (
          <Tooltip title={t('graph.activeRelationshipsOnlyHelp')} describeChild>
            <FormControlLabel
              control={<Switch size="small" checked={activeRelationshipsOnly} disabled={disabled}
                onChange={(_, checked) => onActiveRelationshipsOnlyChange(checked)} />}
              label={<Typography variant="caption" sx={{ color: textColor }}>{t('graph.activeRelationshipsOnly')}</Typography>}
              sx={{ margin: 0 }}
            />
          </Tooltip>
        )}

        </>
      )}
      {showFilterButtonsValue && selectedDate && onDateChange && (
        <Chip size="small" label={t('graph.asOfDate', { date: selectedDate })} onClick={handleTimeSliderClick} onDelete={() => onDateChange(null)} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto' }}>
        <Button ref={moreButtonRef} size="small" startIcon={<MoreHorizIcon />} endIcon={<ExpandMoreIcon />}
          onClick={(event) => setMoreAnchorEl(moreAnchorEl ? null : event.currentTarget)}
          aria-haspopup="menu" aria-expanded={Boolean(moreAnchorEl)} aria-controls={moreAnchorEl ? 'graph-more-menu' : undefined}
          sx={{ textTransform: 'none', color: 'text.primary' }}>
          {t('graph.more')}
        </Button>
        {showFilterButtonsValue && props.onToggleFullscreen && renderToolbarButton('f', t(props.isFullscreen ? 'graph.exitFullscreen' : 'graph.fullscreen'), props.isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />, props.onToggleFullscreen, fullscreenButtonRef, disabled, undefined, true)}
      </Box>
      <Menu id="graph-more-menu" anchorEl={moreAnchorEl} open={Boolean(moreAnchorEl)} onClose={() => setMoreAnchorEl(null)}>
        {showFilterButtonsValue && props.onToggleSearch && renderMoreItem('s', t('graph.search'), <SearchIcon />, props.onToggleSearch)}
        {showFilterButtonsValue && renderMoreItem('x', t('graph.badLinks'), null, handleBadLinksToggle, disabled, showBadLinks)}
        {props.showNewAdditions && props.onClearNewAdditions && renderMoreItem('c', t('graph.clearExpandColor'), <RestoreIcon />, props.onClearNewAdditions)}
        {showFilterButtonsValue && <Divider />}
        {showFilterButtonsValue && renderMoreItem('p', t('graph.physicsSettings'), <SettingsIcon />, handlePhysicsClick)}
        {showFilterButtonsValue && renderMoreItem('d', t('graph.export'), <DownloadIcon />, handleExportClick, disabled)}
        {showFilterButtonsValue && renderMoreItem('h', t('graph.documentation'), <HelpOutlineIcon />, handleHelpToggle)}
      </Menu>

      {/* Render Legend when toggled on */}
      {showLegend && (
      <Box 
        style={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        }}
        sx={{
        position: 'absolute',
        top: '100%',
        marginTop: '10px',
        left: legendLeftOffset,
        zIndex: 1000,
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        borderRadius: '8px',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
        minWidth: '400px',
        transform: 'scale(0.85)',
        transformOrigin: 'top left',
        fontSize: '0.85rem',
        transition: 'left 0.3s ease',
        '& .legend-table th': {
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1) !important' : '#f2f2f2 !important',
        color: isDarkMode ? '#ffffff !important' : 'inherit',
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#ddd'
        },
        '& .legend-table td': {
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#ddd',
        color: isDarkMode ? '#ffffff !important' : 'inherit'
        }
      }}>
        <Legend
          enableCompanyComments={enableCompanyComments}
          showCompanyComments={showCompanyComments}
          onToggleCompanyComments={onToggleCompanyComments}
          enableIvoLicenses={enableIvoLicenses}
          enableLeasedCars={enableLeasedCars}
          enableTenantCases={enableTenantCases}
        />
      </Box>
      )}

      <Popover
        id={physicsId}
        open={openPhysics}
        anchorEl={physicsAnchorEl}
        onClose={handlePhysicsClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
        }}
      >
        <PhysicsSettings settings={physicsSettings} onSettingsChange={onPhysicsSettingsChange} />
      </Popover>

      <Popover
        id={exportId}
        open={openExport}
        anchorEl={exportAnchorEl}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
            sx: {
                p: 2,
                width: 300,
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }
        }}
      >
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            {t('graph.exportSettings')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('graph.exportScale', { scale: exportScale })}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, mt: 1 }}>
            <RadioGroup
                row
                name="export-quality"
                value={exportScale}
                onChange={(e) => setExportScale(Number(e.target.value))}
            >
                {[
                    { label: '1x', value: 1 },
                    { label: '2x', value: Math.max(1, Math.round(maxScale * 0.2)) },
                    { label: '5x', value: Math.max(1, Math.round(maxScale * 0.5)) },
                    { label: '10x', value: maxScale }
                ].map((opt) => (
                    <FormControlLabel
                        key={opt.label}
                        value={opt.value}
                        control={<Radio size="small" />}
                        label={<Typography variant="caption">{opt.label}</Typography>}
                        sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                ))}
            </RadioGroup>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button 
                onClick={handleExportSVG}
                disabled={isExporting}
                variant="outlined" 
                size="small"
                startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            >
                {isExporting ? t('graph.generating') : 'SVG'}
            </Button>
            <Button 
                onClick={handleExportSubmit}
                disabled={isExporting}
                variant="contained" 
                size="small"
                startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            >
                {isExporting ? t('graph.generating') : 'PNG'}
            </Button>
        </Box>
      </Popover>

      {showFilterButtonsValue && openTimeSlider && minDate && maxDate && onDateChange && (
        <Paper
          id={timeSliderId}
          role="region"
          aria-label={t('graph.timeFilter')}
          variant="outlined"
          sx={{ flexBasis: '100%', minWidth: 0, p: 1.5, borderRadius: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2">{t('graph.timeFilter')}</Typography>
            <IconButton size="small" aria-label={t('common.actions.close')} onClick={handleTimeSliderClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <TimeSliderPanel
            minDate={minDate}
            maxDate={maxDate}
            selectedDate={selectedDate || null}
            onDateChange={onDateChange}
            isDarkMode={isDarkMode}
          />
        </Paper>
      )}

      <Dialog 
        open={showHelp} 
        onClose={() => setShowHelp(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogContent>
          <Documentation />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHelp(false)}>{t('common.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Combined Label Settings Menu */}
      <Menu
        anchorEl={labelAnchorEl}
        open={Boolean(labelAnchorEl)}
        onClose={handleLabelClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
        }}
      >
        {/* Toggle-all actions */}
        <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          size="small"
          variant="text"
          onClick={() => {
          if (!props.nodeLabelSettings || !props.edgeLabelSettings) return;
          const nodeVals = Object.values(props.nodeLabelSettings);
          const edgeVals = Object.values(props.edgeLabelSettings);
          const turnOn = nodeVals.some(v => !v) || edgeVals.some(v => !v);
          const newNodes = Object.keys(props.nodeLabelSettings).reduce((acc, k) => {
            acc[k as keyof NodeLabelSettings] = turnOn;
            return acc;
          }, {} as NodeLabelSettings);
          const newEdges = Object.keys(props.edgeLabelSettings).reduce((acc, k) => {
            acc[k as keyof EdgeLabelSettings] = turnOn;
            return acc;
          }, {} as EdgeLabelSettings);
          props.onNodeLabelSettingsChange?.(newNodes);
          props.onEdgeLabelSettingsChange?.(newEdges);
          }}
          sx={{ textTransform: 'none' }}
        >
          {t('graph.labels.toggleAll')}
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => {
          if (!props.nodeLabelSettings) return;
          const vals = Object.values(props.nodeLabelSettings);
          const turnOn = vals.some(v => !v);
          const newNodes = Object.keys(props.nodeLabelSettings).reduce((acc, k) => {
            acc[k as keyof NodeLabelSettings] = turnOn;
            return acc;
          }, {} as NodeLabelSettings);
          props.onNodeLabelSettingsChange?.(newNodes);
          }}
          sx={{ textTransform: 'none' }}
        >
          {t('graph.labels.toggleNodes')}
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => {
          if (!props.edgeLabelSettings) return;
          const vals = Object.values(props.edgeLabelSettings);
          const turnOn = vals.some(v => !v);
          const newEdges = Object.keys(props.edgeLabelSettings).reduce((acc, k) => {
            acc[k as keyof EdgeLabelSettings] = turnOn;
            return acc;
          }, {} as EdgeLabelSettings);
          props.onEdgeLabelSettingsChange?.(newEdges);
          }}
          sx={{ textTransform: 'none' }}
        >
          {t('graph.labels.toggleRelationships')}
        </Button>
        </Box>

        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 3, minWidth: 450 }}>
        {/* Node Labels Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
          {t('graph.labels.nodes')}
          </Typography>
          <MenuItem onClick={() => handleNodeLabelToggle('showOrganizationNumber')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showOrganizationNumber ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('common.fields.organizationNumber')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showOrganizationName')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showOrganizationName ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('shortestPath.organizationName')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showKonkursLikvidation')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showKonkursLikvidation ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('graph.labels.bankruptcyLiquidation')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showSkuldsaldo')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showSkuldsaldo ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('graph.labels.kfmDebt')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showAddress')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showAddress ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('graph.address')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showName')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showName ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('graph.labels.customLinkingTitle')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showFunktionarNamn')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showFunktionarNamn ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('graph.labels.personName')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleNodeLabelToggle('showFunktionarPersonalNumber')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.nodeLabelSettings?.showFunktionarPersonalNumber ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('common.fields.personalNumber')}</Typography>
          </MenuItem>
        </Box>

        {/* Divider */}
        <Box sx={{ width: '1px', bgcolor: 'divider', my: 1 }} />

        {/* Edge Labels Column */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
          {t('graph.labels.relationships')}
          </Typography>
          <MenuItem onClick={() => handleEdgeLabelToggle('showRole')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.edgeLabelSettings?.showRole ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('assessment.details.role')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleEdgeLabelToggle('showDateRange')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.edgeLabelSettings?.showDateRange ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('graph.labels.dateRange')}</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleEdgeLabelToggle('showDuration')} sx={{ py: 0.5 }}>
          <Checkbox 
            checked={props.edgeLabelSettings?.showDuration ?? true} 
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="body2">{t('assessment.details.duration')}</Typography>
          </MenuItem>
        </Box>
        </Box>
      </Menu>
    </div>
  );
};

export default OrgGraphTopPanel;
